// src/App.jsx
// ──────────────────────────────────────────────────────────────
// OMERTÀ — Mafia (React edition)
// Wires together Firebase, game engine, hooks, and screens.
// All UI lives in src/screens/ and src/components/.
// All game logic lives in src/firebase/gameEngine.js.
// ──────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";

// Firebase
import { initFirebase, dbListen, dbGet, dbUpdate, dbRemove } from "./firebase/firebase.js";

// Game engine
import {
  createRoom, joinRoom, resetRoom, deleteRoom,
  startGame, markReady, hostAdvancePhase,
  castMafiaVote, castDoctorVote, castDayVote, confirmDayVote, skipVote,
  sendChatMessage, updateLobbyConfig,
  shouldAutoAdvanceNight, shouldAutoAdvanceSave, shouldAutoAdvanceDay,
} from "./firebase/gameEngine.js";

// Hooks
import { useToast, useLocalMe } from "./hooks/index.js";

// UI primitives
import { Toast, ConnStatus, Announcement } from "./components/ui.jsx";

// Screens
import HomeScreen      from "./screens/HomeScreen.jsx";
import LobbyScreen     from "./screens/LobbyScreen.jsx";
import RoleRevealScreen from "./screens/RoleRevealScreen.jsx";
import GameScreen      from "./screens/GameScreen.jsx";
import WinnerScreen    from "./screens/WinnerScreen.jsx";
import DonateScreen    from "./screens/DonateScreen.jsx";

// Utils
import { uid, genCode, injectGlobalStyles } from "./utils/helpers.js";
import GLOBAL_CSS from "./utils/globalStyles.js";
import { PHASES, VOTE_DURATION, DOCTOR_SKIP_DELAY } from "./utils/constants.js";

// ──────────────────────────────────────────────────────────────
// INITIAL STATE SHAPES
// ──────────────────────────────────────────────────────────────
const INIT_ME = {
  id: null, name: "", isHost: false,
  role: null, alive: true, roomCode: null,
};
const INIT_STATE = {
  players: [], phase: PHASES.LOBBY, round: 1,
  mafiaVotes: {}, doctorVotes: {}, dayVotes: {},
  confirmedVotes: [], skippedVotes: [],
  nightKilled: null, savedId: null,
  lastAnnouncement: null, announcementId: null,
  dayAnnouncement: null, dayAnnouncementId: null,
  winner: null, revealReady: [],
  timerEndsAt: null, timerDuration: VOTE_DURATION,
  lobbyMafia: 1, lobbyDoctor: 1,
  doctorSkipAt: null,
};
const INIT_CHAT = { all: [], night: [], save: [], day: [] };

// ──────────────────────────────────────────────────────────────

export default function App() {
  // Inject styles + favicon once
  useEffect(() => injectGlobalStyles(GLOBAL_CSS), []);

  // ── Firebase readiness ──────────────────────────────────────
  const [fbReady,     setFbReady]    = useState(false);
  const [connStatus,  setConnStatus] = useState("connecting");

  // ── App state ───────────────────────────────────────────────
  const [screen,        setScreen]       = useState("home");
  const [me,            setMe]           = useState(INIT_ME);
  const [gameState,     setGameState]    = useState(INIT_STATE);
  const [localChat,     setLocalChat]    = useState(INIT_CHAT);
  const [announcement,  setAnnouncement] = useState(null);

  // ── Stable refs (avoid stale closures in Firebase callbacks) ──
  const meRef         = useRef(me);
  const gameStateRef  = useRef(gameState);
  const annShownRef   = useRef(null);
  const dayAnnShownRef= useRef(null);
  const chatUnsubsRef = useRef({});
  const stateUnsubRef = useRef(null);
  const deletionTimer = useRef(null);
  const doctorSkipTimer = useRef(null);   // host-side 10-s doctor-skip timer
  const resultAdvanceTimer = useRef(null);// host-side RESULT → NIGHT fallback

  useEffect(() => { meRef.current = me; },        [me]);
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);

  // ── Toast ────────────────────────────────────────────────────
  const { toasts, toast } = useToast();

  // ── Session persistence ──────────────────────────────────────
  const { load: loadMe, save: saveMe } = useLocalMe();

  const updateMe = useCallback((partial) => {
    setMe((prev) => {
      const next = { ...prev, ...partial };
      meRef.current = next;
      saveMe(next);
      return next;
    });
  }, [saveMe]);

  // ──────────────────────────────────────────────────────────────
  // FIREBASE INIT
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    initFirebase().then((ok) => {
      setFbReady(ok);
      setConnStatus(ok ? "online" : "offline");
      if (ok) tryRestoreSession();
    });
    return () => detachRoom();
  }, []);

  function tryRestoreSession() {
    const saved = loadMe();
    if (saved?.roomCode) {
      meRef.current = saved;
      setMe(saved);
      attachRoom(saved.roomCode);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // ROOM ATTACH / DETACH
  // ──────────────────────────────────────────────────────────────
  function detachRoom() {
    if (stateUnsubRef.current) { stateUnsubRef.current(); stateUnsubRef.current = null; }
    Object.values(chatUnsubsRef.current).forEach((u) => u && u());
    chatUnsubsRef.current = {};
  }

  function attachRoom(code) {
    detachRoom();

    // ── State listener ────────────────────────────────────────
    stateUnsubRef.current = dbListen(`rooms/${code}/state`, (snap) => {
      // Room was deleted (host quit / 5-min cleanup).
      // Non-host players: detach and return to home.
      if (!snap.exists()) {
        if (!meRef.current.roomCode) return;
        detachRoom();
        clearTimeout(deletionTimer.current);
        clearTimeout(doctorSkipTimer.current);
        clearTimeout(resultAdvanceTimer.current);
        const blank = { ...INIT_ME };
        meRef.current = blank;
        setMe(blank);
        saveMe(blank);
        setGameState(INIT_STATE);
        setLocalChat(INIT_CHAT);
        setAnnouncement(null);
        setScreen("home");
        toast("Room closed by host.", "info");
        return;
      }

      const incoming = snap.val();
      incoming.confirmedVotes = incoming.confirmedVotes || [];
      incoming.skippedVotes   = incoming.skippedVotes   || [];
      incoming.revealReady    = incoming.revealReady    || [];

      const prevPhase  = gameStateRef.current.phase;
      const nowLobby   = incoming.phase === PHASES.LOBBY;

      // Transition ended → lobby = play again reset
      if (prevPhase === PHASES.ENDED && nowLobby) {
        setLocalChat(INIT_CHAT);
        annShownRef.current    = null;
        dayAnnShownRef.current = null;
        setAnnouncement(null);
        clearTimeout(deletionTimer.current);
        clearTimeout(doctorSkipTimer.current);
        clearTimeout(resultAdvanceTimer.current);
      }

      // Sync own player info
      const cur_me   = meRef.current;
      const mePlayer = (incoming.players || []).find((p) => p.id === cur_me.id);
      if (mePlayer) {
        updateMe({ role: mePlayer.role, alive: mePlayer.alive });
      }

      setGameState(incoming);
      gameStateRef.current = incoming;

      // ── Screen routing ──
      if (incoming.phase === PHASES.LOBBY)  setScreen("lobby");
      else if (incoming.phase === PHASES.REVEAL) setScreen("reveal");
      else if (incoming.phase === PHASES.ENDED)  setScreen("winner");
      else setScreen("game");

      // ── Show night kill announcement when entering day ──
      if (
        incoming.phase === PHASES.DAY &&
        incoming.lastAnnouncement &&
        incoming.announcementId &&
        incoming.announcementId !== annShownRef.current
      ) {
        annShownRef.current = incoming.announcementId;
        setTimeout(() => setAnnouncement(incoming.lastAnnouncement), 350);
      }

      // ── Show day vote announcement when entering result ──
      if (
        incoming.phase === PHASES.RESULT &&
        incoming.dayAnnouncement &&
        incoming.dayAnnouncementId &&
        incoming.dayAnnouncementId !== dayAnnShownRef.current
      ) {
        dayAnnShownRef.current = incoming.dayAnnouncementId;
        setTimeout(() => setAnnouncement(incoming.dayAnnouncement), 350);
      }

      // ── Show win announcement when game ends ──
      if (incoming.phase === PHASES.ENDED && incoming.winner) {
        const annType = incoming.winner === "mafia" ? "mafia-win" : "town-win";
        setTimeout(() => setAnnouncement({ type: annType, text: "" }), 500);
        scheduleRoomDeletion(code);
      }

      // ── HOST-ONLY auto-advance logic ──────────────────────
      if (!meRef.current.isHost) return;

      if (incoming.phase === PHASES.NIGHT && shouldAutoAdvanceNight(incoming)) {
        setTimeout(() => {
          if (gameStateRef.current.phase === PHASES.NIGHT) {
            hostAdvancePhase(code, gameStateRef.current);
          }
        }, 1400);
      }

      if (incoming.phase === PHASES.SAVE) {
        // Clear any existing doctor-skip timer
        clearTimeout(doctorSkipTimer.current);

        if (shouldAutoAdvanceSave(incoming)) {
          setTimeout(() => {
            if (gameStateRef.current.phase === PHASES.SAVE) {
              hostAdvancePhase(code, gameStateRef.current);
            }
          }, 1400);
        } else if (incoming.doctorSkipAt) {
          // All doctors are dead — auto-skip after doctorSkipAt
          const delay = Math.max(0, incoming.doctorSkipAt - Date.now());
          doctorSkipTimer.current = setTimeout(() => {
            if (gameStateRef.current.phase === PHASES.SAVE) {
              hostAdvancePhase(code, gameStateRef.current);
            }
          }, delay);
        }
      }

      if (incoming.phase === PHASES.DAY && shouldAutoAdvanceDay(incoming)) {
        setTimeout(() => {
          if (gameStateRef.current.phase === PHASES.DAY) {
            hostAdvancePhase(code, gameStateRef.current);
          }
        }, 1400);
      }

      // RESULT phase: advance to next NIGHT after 4.5 s so players have
      // time to read the announcement. Covers all outcomes (elimination,
      // tie, no-vote). Re-entering RESULT simply resets the timer.
      if (incoming.phase === PHASES.RESULT) {
        clearTimeout(resultAdvanceTimer.current);
        resultAdvanceTimer.current = setTimeout(() => {
          if (gameStateRef.current.phase === PHASES.RESULT) {
            hostAdvancePhase(code, gameStateRef.current);
          }
        }, 4500);
      }

      // Timer expiry auto-advance (host resolves when timer hits 0)
      if (
        incoming.timerEndsAt &&
        [PHASES.NIGHT, PHASES.SAVE, PHASES.DAY].includes(incoming.phase)
      ) {
        const remaining = incoming.timerEndsAt - Date.now();
        if (remaining <= 0) {
          // Timer already expired — advance now
          hostAdvancePhase(code, incoming);
        }
      }
    });

    // ── Chat listeners (one per channel) ──────────────────────
    ["all", "day", "night", "save"].forEach((ch) => {
      chatUnsubsRef.current[ch] = dbListen(`rooms/${code}/chat/${ch}`, (snap) => {
        const msgs = [];
        if (snap.exists()) snap.forEach((child) => { msgs.push(child.val()); });
        setLocalChat((prev) => ({ ...prev, [ch]: msgs }));
      });
    });

    setConnStatus("online");
  }

  // ──────────────────────────────────────────────────────────────
  // ROOM DELETION (5-min auto-clean after game ends)
  // ──────────────────────────────────────────────────────────────
  function scheduleRoomDeletion(code) {
    if (!meRef.current.isHost) return;
    clearTimeout(deletionTimer.current);
    deletionTimer.current = setTimeout(async () => {
      if (gameStateRef.current.phase === PHASES.ENDED) {
        detachRoom();
        await deleteRoom(code);
      }
    }, 5 * 60 * 1000);
  }

  // ──────────────────────────────────────────────────────────────
  // HANDLERS — HOME
  // ──────────────────────────────────────────────────────────────
  async function handleCreateRoom(name) {
    if (!name.trim()) { toast("Enter your name", "error"); return; }
    if (!fbReady)     { toast("Connecting...", "error"); return; }

    const id         = uid();
    const { code, state } = await createRoom(id, name.trim());

    const newMe = { id, name: name.trim(), isHost: true, role: null, alive: true, roomCode: code };
    meRef.current = newMe;
    setMe(newMe);
    saveMe(newMe);
    setGameState(state);
    gameStateRef.current = state;
    setLocalChat(INIT_CHAT);

    attachRoom(code);
    setScreen("lobby");
    toast(`Room created! Code: ${code}`, "success");
  }

  

  async function handleJoinRoom(name, code) {
    if (!name.trim())   { toast("Enter your name",          "error"); return; }
    if (code.length < 4){ toast("Enter a valid room code",  "error"); return; }
    if (!fbReady)       { toast("Connecting...",            "error"); return; }

    const id     = uid();
    const result = await joinRoom(code, id, name.trim());

    if (result.error) { toast(result.error, "error"); return; }

    const player = result.me;
    const newMe  = {
      id:       player.id,
      name:     player.name,
      isHost:   player.isHost || false,
      role:     player.role,
      alive:    player.alive,
      roomCode: code,
    };
    meRef.current = newMe;
    setMe(newMe);
    saveMe(newMe);
    setGameState(result.state);
    gameStateRef.current = result.state;
    setLocalChat(INIT_CHAT);

    attachRoom(code);
    if (result.rejoined) toast(`Rejoined as ${player.name}`, "success");
    setScreen("lobby");
  }

  // ──────────────────────────────────────────────────────────────
  // HANDLERS — LOBBY
  // ──────────────────────────────────────────────────────────────
  function handleCopyCode() {
    navigator.clipboard.writeText(meRef.current.roomCode || "")
      .then(() => toast("Code copied!", "success"))
      .catch(() => toast("Copy failed", "error"));
  }

  async function handleMafiaChange(val) {
    const newSt = { ...gameStateRef.current, lobbyMafia: val };
    setGameState(newSt);
    await updateLobbyConfig(meRef.current.roomCode, val, newSt.lobbyDoctor || 1);
  }
  async function handleDoctorChange(val) {
    const newSt = { ...gameStateRef.current, lobbyDoctor: val };
    setGameState(newSt);
    await updateLobbyConfig(meRef.current.roomCode, newSt.lobbyMafia || 1, val);
  }

  async function handleStartGame() {
    const result = await startGame(meRef.current.roomCode, gameStateRef.current);
    if (result.error) { toast(result.error, "error"); return; }
    // Firebase onValue will trigger screen change
  }

  // ──────────────────────────────────────────────────────────────
  // HANDLERS — REVEAL
  // ──────────────────────────────────────────────────────────────
  async function handleReady() {
    await markReady(meRef.current.roomCode, gameStateRef.current, meRef.current.id);
  }

  async function handleBeginRound() {
    await hostAdvancePhase(meRef.current.roomCode, gameStateRef.current);
  }

  // ──────────────────────────────────────────────────────────────
  // HANDLERS — GAME VOTES
  // ──────────────────────────────────────────────────────────────
  async function handleMafiaVote(targetId) {
    const cur = meRef.current;
    const st  = gameStateRef.current;
    if (!cur.alive || cur.role !== "mafia" || st.phase !== PHASES.NIGHT) return;
    await castMafiaVote(cur.roomCode, st, cur.id, cur.name, targetId);
  }

  async function handleDoctorVote(targetId) {
    const cur = meRef.current;
    const st  = gameStateRef.current;
    if (!cur.alive || cur.role !== "doctor" || st.phase !== PHASES.SAVE) return;
    await castDoctorVote(cur.roomCode, st, cur.id, cur.name, targetId);
  }

  async function handleDayVote(targetId) {
    const cur = meRef.current;
    const st  = gameStateRef.current;
    if (!cur.alive || st.phase !== PHASES.DAY) return;
    if ((st.confirmedVotes || []).includes(cur.id)) return;
    if ((st.skippedVotes   || []).includes(cur.id)) return;
    await castDayVote(cur.roomCode, st, cur.id, targetId);
  }

  async function handleConfirmDayVote() {
    const cur    = meRef.current;
    const st     = gameStateRef.current;
    if (!cur.alive || st.phase !== PHASES.DAY) return;
    const result = await confirmDayVote(cur.roomCode, st, cur.id, cur.name);
    if (result?.error) toast(result.error, "error");
  }

  async function handleSkipVote() {
    const cur = meRef.current;
    const st  = gameStateRef.current;
    if (!cur.alive) return;
    await skipVote(cur.roomCode, st, cur.id, cur.name, st.phase);
  }

  // ──────────────────────────────────────────────────────────────
  // HANDLERS — CHAT
  // ──────────────────────────────────────────────────────────────
  async function handleSendChat(channel, text) {
    const cur = meRef.current;
    await sendChatMessage(cur.roomCode, channel, cur.name, cur.role, text);
  }

  // ──────────────────────────────────────────────────────────────
  // HANDLERS — PLAY AGAIN (robust rewrite)
  // ──────────────────────────────────────────────────────────────
  async function handlePlayAgain() {
    const cur = meRef.current;
    if (!cur.roomCode || !cur.isHost) return;

    // Cancel auto-deletion and pending advance timers
    clearTimeout(deletionTimer.current);
    clearTimeout(doctorSkipTimer.current);
    clearTimeout(resultAdvanceTimer.current);

    // Reset trackers
    annShownRef.current    = null;
    dayAnnShownRef.current = null;
    setAnnouncement(null);
    setLocalChat(INIT_CHAT);

    // Atomically rewrite room — triggers all clients' onValue with phase:'lobby'
    const fresh = await resetRoom(cur.roomCode, gameStateRef.current);

    // Update host's own role
    updateMe({ role: null, alive: true });
    setGameState(fresh);
    gameStateRef.current = fresh;
    // Screen will update via onValue listener
  }
  async function handleQuitRoom() {
  const cur = meRef.current;
  if (!cur.roomCode) return;
  detachRoom();
  clearTimeout(deletionTimer.current);
  clearTimeout(doctorSkipTimer.current);
  clearTimeout(resultAdvanceTimer.current);
  if (cur.isHost) await deleteRoom(cur.roomCode);
  const blank = { ...INIT_ME };
  meRef.current = blank;
  setMe(blank);
  saveMe(blank);
  setGameState(INIT_STATE);
  setLocalChat(INIT_CHAT);
  setAnnouncement(null);
  setScreen("home");
}

  // ──────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative", zIndex: 1, minHeight: "100vh" }}>

      {screen === "home" && (
        <HomeScreen
          onCreateRoom={handleCreateRoom}
          onJoinRoom={handleJoinRoom}
          onOpenDonate={() => setScreen("donate")}
        />
      )}

      {screen === "donate" && (
        <DonateScreen onBack={() => setScreen("home")} />
      )}

      {screen === "lobby" && (
        <LobbyScreen
          state={gameState} me={me}
          onStartGame={handleStartGame}
          onCopyCode={handleCopyCode}
          onMafiaChange={handleMafiaChange}
          onDoctorChange={handleDoctorChange}
          onQuitRoom={handleQuitRoom}
        />
      )}

      {screen === "reveal" && (
        <RoleRevealScreen
          state={gameState} me={me}
          onReady={handleReady}
          onBeginRound={handleBeginRound}
        />
      )}

      {screen === "game" && (
        <GameScreen
          state={gameState} me={me}
          localChat={localChat}
          onSendChat={handleSendChat}
          onMafiaVote={handleMafiaVote}
          onDoctorVote={handleDoctorVote}
          onDayVote={handleDayVote}
          onConfirmDayVote={handleConfirmDayVote}
          onSkipVote={handleSkipVote}
          onQuitRoom={handleQuitRoom}
        />
      )}

      {screen === "winner" && (
        <WinnerScreen
          state={gameState} me={me}
          onPlayAgain={handlePlayAgain}
          onQuit={handleQuitRoom}
        />
      )}

      {/* Overlays */}
      {announcement && (
        <Announcement ann={announcement} onClose={() => setAnnouncement(null)} />
      )}
      <Toast toasts={toasts} />
      <ConnStatus status={connStatus} code={me.roomCode} />
    </div>
  );
}
